<template>
  <div class="pos-input">
    <!-- Input Fields -->
    <div class="pos-input-content">
      <div class="pos-input-grid">
        <!-- Row 1: Barcode | Pramuniaga | Customer -->
        <div class="input-barcode">
          <div class="input-group">
            <InputText
              type="text"
              v-model="currentBarcode"
              placeholder="Scan atau ketik barcode"
              :disabled="!isOpenShift"
              @focus="$event.target.select()"
              @keyup.enter="$emit('search-barcode')"
              v-shortkey.focus="['f2']"
              ref="barcode"
              class="input-field"
            />
          </div>
        </div>

        <div class="input-pramuniaga">
          <div class="input-group">
            <AutoComplete
              :suggestions="salespersonsFiltered"
              @complete="$emit('search-salesperson', $event)"
              v-model="currentSalespersonSelected"
              placeholder="Pilih pramuniaga"
              field="name"
              :dropdown="true"
              :disabled="!isOpenShift"
              id="pramuniaga"
              class="input-field"
            />
          </div>
        </div>

        <div class="input-customer">
          <div class="input-group">
            <AutoComplete
              :suggestions="customersWithDisplayField"
              @complete="$emit('search-customer')"
              v-model="currentCustomerSelected"
              placeholder="Pilih pelanggan"
              field="displayField"
              @keyup="$emit('search-customer')"
              :dropdown="true"
              :disabled="!isOpenShift"
              id="customer"
              class="input-field"
            >
              <template #item="slotProps">
                <div v-if="slotProps.index == 0" class="add-customer-option">
                  <Button
                    @click="$emit('create-customer')"
                    label="Tambah Customer"
                    icon="pi pi-plus"
                    class="p-button-primary p-button-sm"
                    :disabled="!isOpenShift || totalBilledAmount < 0"
                  />
                </div>
                <div v-if="slotProps.item" class="customer-option">
                  <span
                    v-if="slotProps.index == 0"
                    class="customer-list-header"
                  >
                    Daftar Customer
                  </span>
                  <span
                    v-if="
                      slotProps.item.name && slotProps.item.name.trim() !== ''
                    "
                  >
                    {{ slotProps.item.name }} - {{ slotProps.item.phone }}
                  </span>
                  <span v-else-if="slotProps.item.phone">
                    {{ slotProps.item.phone }}
                  </span>
                </div>
              </template>
            </AutoComplete>

            <!-- Customer Error State -->
            <div v-if="isErrorCustomers" class="customer-error">
              <div class="error-message">
                <p @click="$emit('create-customer')">
                  Data tidak ditemukan, buat baru?
                  <i
                    class="pi pi-arrow-circle-right"
                    @click="$emit('create-customer')"
                  />
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons (span both rows) -->
        <div class="button-simpan">
          <Button
            v-shortkey="['shift', 's']"
            @shortkey="$emit('save-transaction', false)"
            @click="$emit('save-transaction', false)"
            :label="loadingTransaction ? 'Tunggu...' : 'Simpan'"
            class="p-button-outlined action-button"
            :disabled="!isOpenShift || loadingTransaction"
          />
        </div>

        <div class="button-bayar">
          <Button
            @click="$emit('save-transaction', true)"
            v-shortkey="['space']"
            @shortkey="$emit('save-transaction', true)"
            :label="loadingTransaction ? 'Tunggu...' : 'Bayar'"
            class="p-button-primary action-button"
            :disabled="
              !isOpenShift || totalBilledAmount < 0 || loadingTransaction
            "
          />
        </div>

        <!-- Row 2: Discount | Tipe Diskon | Voucher with Inline Button -->
        <div class="input-discount">
          <div class="input-group">
            <InputNumber
              placeholder="0"
              mode="decimal"
              id="jumlah-diskon"
              locale="id-ID"
              ref="discountTransactionPercentage"
              v-model="currentDiscountTransactionPercentage"
              @focus="handleDiscountFocus"
              :disabled="
                !isOpenShift ||
                currentDiscountType === null ||
                (currentDiscountType != 'Rupiah' &&
                  currentDiscountType != 'Persen')
              "
              @input="$emit('change-discount-transaction')"
              @input-number="$emit('change-discount-transaction')"
              class="input-field"
            />
          </div>
        </div>

        <div class="input-tipe-diskon">
          <div class="input-group">
            <Dropdown
              v-model="currentDiscountType"
              :options="discountTypes"
              placeholder="Pilih tipe diskon"
              :disabled="subTotal == '0'"
              @change="$emit('change-discount-transaction')"
              class="input-field"
            />
          </div>
        </div>

        <div class="input-voucher-inline">
          <div class="input-group">
            <div class="voucher-input-group">
              <InputText
                type="text"
                placeholder="Masukkan kode voucher"
                @focus.native="$event.target.select()"
                v-model="currentVoucherCode"
                :disabled="!isOpenShift || subTotal == '0'"
                class="input-field voucher-input"
              />
              <Button
                @click="$emit('validate-voucher')"
                label="Cek"
                class="inline-button"
                :disabled="
                  !isOpenShift ||
                  subTotal == '0' ||
                  currentVoucherCode.length <= 0
                "
                v-show="!cancelVoucher"
              />
              <Button
                @click="$emit('clear-voucher')"
                label="Batal"
                class="inline-button p-button-danger"
                :disabled="!isOpenShift || subTotal == '0'"
                v-show="cancelVoucher"
              />
            </div>
          </div>
        </div>

        <!-- Empty spaces for grid alignment -->
        <div class="empty-space-1"></div>
        <div class="empty-space-2"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'POSInput',
  props: {
    barcode: {
      type: String,
      default: ''
    },
    salespersonsFiltered: {
      type: Array,
      default: () => []
    },
    salespersonSelected: {
      type: Object,
      default: null
    },
    customers: {
      type: Array,
      default: () => []
    },
    customerSelected: {
      type: Object,
      default: null
    },
    isErrorCustomers: {
      type: Boolean,
      default: false
    },
    discountTransactionPercentage: {
      type: Number,
      default: null
    },
    discountType: {
      type: String,
      default: null
    },
    discountTypes: {
      type: Array,
      default: () => ['Persen', 'Rupiah']
    },
    voucherCode: {
      type: String,
      default: ''
    },
    cancelVoucher: {
      type: Boolean,
      default: false
    },
    isOpenShift: {
      type: Boolean,
      default: false
    },
    subTotal: {
      type: Number,
      default: 0
    },
    totalBilledAmount: {
      type: Number,
      default: 0
    },
    loadingTransaction: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localSalespersonSelected: this.salespersonSelected,
      localDiscountType: this.discountType,
      localDiscountTransactionPercentage: this.discountTransactionPercentage,
      localBarcode: this.barcode,
      localCustomerSelected: this.customerSelected,
      localVoucherCode: this.voucherCode
    }
  },
  watch: {
    salespersonSelected(newVal) {
      this.localSalespersonSelected = newVal
    },
    discountType(newVal) {
      this.localDiscountType = newVal
      // When discount type changes, re-attach listener if type is valid
      this.$nextTick(() => {
        if (newVal && (newVal === 'Rupiah' || newVal === 'Persen')) {
          this.attachDiscountInputListener()
        } else {
          // Clean up listener if discount type is cleared or invalid
          this.cleanupDiscountInputListener()
        }
      })
    },
    discountTransactionPercentage(newVal) {
      this.localDiscountTransactionPercentage = newVal
    },
    barcode(newVal) {
      this.localBarcode = newVal
    },
    customerSelected(newVal) {
      this.localCustomerSelected = newVal
    },
    voucherCode(newVal) {
      this.localVoucherCode = newVal
    }
  },
  computed: {
    currentBarcode: {
      get() {
        return this.localBarcode
      },
      set(value) {
        this.localBarcode = value
        this.$emit('update:barcode', value)
      }
    },
    currentSalespersonSelected: {
      get() {
        return this.localSalespersonSelected
      },
      set(value) {
        this.localSalespersonSelected = value
        this.$emit('update:salesperson-selected', value)
      }
    },
    currentDiscountType: {
      get() {
        return this.localDiscountType
      },
      set(value) {
        this.localDiscountType = value
        this.$emit('update:discount-type', value)
      }
    },
    currentDiscountTransactionPercentage: {
      get() {
        return this.localDiscountTransactionPercentage
      },
      set(value) {
        this.localDiscountTransactionPercentage = value
        this.$emit('update:discount-transaction-percentage', value)
      }
    },
    currentCustomerSelected: {
      get() {
        if (!this.localCustomerSelected) return null

        // If it's already from customersWithDisplayField, return as is
        if (this.localCustomerSelected.displayField) {
          return this.localCustomerSelected
        }

        // Otherwise, find the matching customer in customersWithDisplayField
        const matchingCustomer = this.customersWithDisplayField.find(
          customer => customer.id === this.localCustomerSelected.id
        )
        return matchingCustomer || this.localCustomerSelected
      },
      set(value) {
        // Store the original customer object (without displayField)
        const originalCustomer = this.customers.find(
          customer => customer.id === value.id
        )
        this.localCustomerSelected = originalCustomer || value
        this.$emit('update:customer-selected', this.localCustomerSelected)
      }
    },
    customersWithDisplayField() {
      return this.customers.map(customer => ({
        ...customer,
        displayField:
          customer.name && customer.name.trim() !== ''
            ? `${customer.name} - ${customer.phone}`
            : customer.phone || ''
      }))
    },
    currentVoucherCode: {
      get() {
        return this.localVoucherCode
      },
      set(value) {
        this.localVoucherCode = value
        this.$emit('update:voucher-code', value)
      }
    }
  },
  methods: {
    attachDiscountInputListener() {
      const discountRef = this.$refs.discountTransactionPercentage
      if (!discountRef || !discountRef.$el) return

      const inputElement = discountRef.$el.querySelector('input')
      if (!inputElement) return

      // Remove existing listener to avoid duplicates
      const existingHandler = inputElement._discountInputHandler
      if (existingHandler) {
        inputElement.removeEventListener('keyup', existingHandler)
        inputElement.removeEventListener('input', existingHandler)
        inputElement.removeEventListener('keydown', existingHandler)
      }

      // Create handler that reads value directly and triggers calculation
      const handler = e => {
        const rawValue = e.target.value
        if (rawValue !== undefined && rawValue !== '') {
          // Parse the value (remove formatting like dots, commas from locale)
          const cleaned = String(rawValue)
            .replace(/\./g, '')
            .replace(/,/g, '')
            .trim()
          const numValue = cleaned === '' ? null : parseFloat(cleaned) || null

          // Update the value using computed setter for reactivity
          if (this.currentDiscountTransactionPercentage !== numValue) {
            this.currentDiscountTransactionPercentage = numValue
            // Trigger calculation immediately
            this.$nextTick(() => {
              this.$emit('change-discount-transaction')
            })
          }
        } else if (
          rawValue === '' &&
          this.currentDiscountTransactionPercentage !== null
        ) {
          // Handle empty input
          this.currentDiscountTransactionPercentage = null
          this.$nextTick(() => {
            this.$emit('change-discount-transaction')
          })
        }
      }

      // Store handler for cleanup
      inputElement._discountInputHandler = handler

      // Attach listeners for real-time updates
      inputElement.addEventListener('keyup', handler)
      inputElement.addEventListener('input', handler)
      inputElement.addEventListener('keydown', handler)
    },
    cleanupDiscountInputListener() {
      const discountRef = this.$refs.discountTransactionPercentage
      if (!discountRef || !discountRef.$el) return

      const inputElement = discountRef.$el.querySelector('input')
      if (inputElement && inputElement._discountInputHandler) {
        inputElement.removeEventListener(
          'keyup',
          inputElement._discountInputHandler
        )
        inputElement.removeEventListener(
          'input',
          inputElement._discountInputHandler
        )
        inputElement.removeEventListener(
          'keydown',
          inputElement._discountInputHandler
        )
        delete inputElement._discountInputHandler
      }
    },
    handleDiscountFocus(event) {
      // Select text on focus
      if (event && event.target) {
        event.target.select()
      }

      // Attach input/keyup listeners to the actual input element for real-time calculation
      // PrimeVue 2.4.2 InputNumber doesn't update v-model on every keystroke
      // We need to access the underlying input element and read value directly
      this.$nextTick(() => {
        this.attachDiscountInputListener()
      })
    }
  },
  mounted() {
    // Attach listener when component mounts if discount type is already set
    this.$nextTick(() => {
      if (
        this.$refs.discountTransactionPercentage &&
        this.currentDiscountType &&
        (this.currentDiscountType === 'Rupiah' ||
          this.currentDiscountType === 'Persen')
      ) {
        this.attachDiscountInputListener()
      }
    })
  },
  beforeDestroy() {
    // Clean up listeners when component is destroyed
    this.cleanupDiscountInputListener()
  }
}
</script>

<style scoped lang="scss">
.pos-input {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Input Header */
.pos-input-header {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.input-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    color: #3b82f6;
  }
}

/* Input Content */
.pos-input-content {
  flex: 1;
  padding: 0;
  display: flex;
  flex-direction: column;
}

/* New CSS Grid Layout */
.pos-input-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 0.5rem;
  height: 100%;
  padding: 0.5rem;
}

/* Grid Areas */
.input-barcode {
  grid-column: 1;
  grid-row: 1;
}

.input-pramuniaga {
  grid-column: 2;
  grid-row: 1;
}

.input-customer {
  grid-column: 3;
  grid-row: 1;
}

.button-simpan {
  grid-column: 4;
  grid-row: 1 / span 2;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ::v-deep .p-button {
    font-size: 1.125rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
}

.button-bayar {
  grid-column: 5;
  grid-row: 1 / span 2;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ::v-deep .p-button {
    font-size: 1.125rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
}

.input-discount {
  grid-column: 1;
  grid-row: 2;
}

.input-tipe-diskon {
  grid-column: 2;
  grid-row: 2;
}

.input-voucher-inline {
  grid-column: 3;
  grid-row: 2;
}

.empty-space-1 {
  grid-column: 4;
  grid-row: 2;
}

.empty-space-2 {
  grid-column: 5;
  grid-row: 2;
}

/* Inline Voucher Button */
.voucher-input-group {
  display: flex;
  gap: 0.25rem;
  align-items: stretch;
}

.voucher-input {
  flex: 1;
}

.inline-button {
  min-width: 3rem;
  padding: 0.5rem;
  font-size: 0.75rem;
  white-space: nowrap;
}

/* PrimeVue Grid Layout (Legacy) */
.p-grid {
  margin-bottom: 0.25rem;
}

.p-grid:last-child {
  margin-bottom: 0;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  height: 100%;
}

.input-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
}

.input-field {
  width: 100%;
}

/* Customer Options */
.add-customer-option {
  padding: 0.5rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.customer-option {
  padding: 0.5rem 0;
}

.customer-list-header {
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  text-align: center;
}

/* Customer Error State */
.customer-error {
  margin-top: 0.5rem;
}

.error-message {
  padding: 0.75rem;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  background: #fef2f2;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: #fee2e2;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
    color: #dc2626;
    display: flex;
    align-items: center;
    justify-content: space-between;

    i {
      font-size: 1.125rem;
      transition: transform 0.15s ease;
    }

    &:hover i {
      transform: translateX(2px);
    }
  }
}

/* Voucher Buttons */
.voucher-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/* Action Buttons */
.action-button {
  width: 100%;
  min-height: 2.25rem;
}

.action-button {
  flex: 1;
  min-height: 2.25rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .pos-input-grid {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto auto auto;
    gap: 0.25rem;
    padding: 0.25rem;
  }

  .input-barcode {
    grid-column: 1;
    grid-row: 1;
  }

  .input-pramuniaga {
    grid-column: 2;
    grid-row: 1;
  }

  .input-customer {
    grid-column: 1 / span 2;
    grid-row: 2;
  }

  .input-discount {
    grid-column: 1;
    grid-row: 3;
  }

  .input-tipe-diskon {
    grid-column: 2;
    grid-row: 3;
  }

  .input-voucher-inline {
    grid-column: 1 / span 2;
    grid-row: 4;
  }

  .button-simpan {
    grid-column: 1;
    grid-row: 5;
  }

  .button-bayar {
    grid-column: 2;
    grid-row: 5;
  }

  .empty-space-1,
  .empty-space-2 {
    display: none;
  }

  .inline-button {
    min-width: 2.5rem;
    padding: 0.375rem;
    font-size: 0.7rem;
  }

  .action-button {
    min-height: 2rem;
  }
}

@media (max-width: 480px) {
  .pos-input-grid {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(8, auto);
    gap: 0.25rem;
  }

  .input-barcode,
  .input-pramuniaga,
  .input-customer,
  .input-discount,
  .input-tipe-diskon,
  .input-voucher-inline,
  .button-simpan,
  .button-bayar {
    grid-column: 1;
  }

  .input-barcode {
    grid-row: 1;
  }
  .input-pramuniaga {
    grid-row: 2;
  }
  .input-customer {
    grid-row: 3;
  }
  .input-discount {
    grid-row: 4;
  }
  .input-tipe-diskon {
    grid-row: 5;
  }
  .input-voucher-inline {
    grid-row: 6;
  }
  .button-simpan {
    grid-row: 7;
  }
  .button-bayar {
    grid-row: 8;
  }

  .voucher-input-group {
    flex-direction: column;
    gap: 0.25rem;
  }

  .inline-button {
    width: 100%;
    min-width: auto;
  }
}

/* Legacy Styles */
.add-customer:hover {
  cursor: pointer;
}
</style>
